<template>
    <div>
        <div class="app-title">
            <h2>{{ pageTitle }}</h2>
        </div>
        <section class="app-content">
            <div class="card my-1">
                <div class="card-body">
                    <b-form>
                        <b-alert :show="!!errorMessage" variant="danger" class="py-2">
                            {{ errorMessage }}
                        </b-alert>

                        <h6 class="text-muted border-bottom pb-1">
                            {{ $t('label.em.asset.emArchFixedAssetChange.assetBlock') }}
                        </h6>
                        <!--
                            搜索框与「已选固定资产」下拉合并成一个输入框：输入即远程搜（debounce 300ms），
                            候选挂在下方浮层里，选中后把「固资号 / 子固资号 资产名称」写回框内。
                            用户一旦继续打字就清掉 form.assetId —— 否则框里的文字与下面那排只读快照会
                            对不上，看着像换了资产、提交的却还是上一条。
                        -->
                        <b-form-row>
                            <b-col md="6">
                                <b-form-group :label="requiredLabel('assetId')">
                                    <div class="asset-combo">
                                        <b-input-group size="sm">
                                            <b-form-input ref="assetInput"
                                                          v-model="keyword"
                                                          size="sm"
                                                          autocomplete="off"
                                                          :placeholder="$t('label.em.asset.emArchFixedAssetChange.assetSearchPlaceholder')"
                                                          @input="onKeywordInput"
                                                          @focus="suggestionsVisible = true"
                                                          @blur="onAssetBlur"
                                                          @keydown.native="onAssetKeydown"></b-form-input>
                                            <b-input-group-append>
                                                <!-- 已选中时按钮变「清除」：再搜一次没有意义，用户要的是换一条 -->
                                                <b-button v-if="form.assetId"
                                                          size="sm" variant="outline-secondary"
                                                          :title="$t('label.em.asset.emArchFixedAssetChange.assetClear')"
                                                          @mousedown.native.prevent
                                                          @click="clearAsset">
                                                    <i class="fa fa-fw fa-times"></i>
                                                </b-button>
                                                <b-button v-else
                                                          size="sm" variant="outline-primary"
                                                          :disabled="searching"
                                                          @mousedown.native.prevent
                                                          @click="openSuggestions">
                                                    <i class="fa fa-fw"
                                                       :class="searching ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                        <div v-if="suggestionsVisible" class="asset-dropdown">
                                            <div v-if="searching" class="asset-item text-muted">
                                                <i class="fa fa-fw fa-spinner fa-spin"></i>
                                            </div>
                                            <div v-else-if="assets.length === 0" class="asset-item text-muted">
                                                {{ $t('label.em.asset.emArchFixedAssetChange.assetNoMatch') }}
                                            </div>
                                            <!-- mousedown.prevent：不让输入框先 blur 把浮层收掉，否则 click 永远打不中 -->
                                            <div v-for="(item, index) in assets"
                                                 v-else
                                                 :key="item.id"
                                                 :class="['asset-item', {'asset-item-active': index === activeIndex}]"
                                                 @mousedown.prevent="selectAsset(item)"
                                                 @mouseenter="activeIndex = index">
                                                <div class="text-truncate">{{ assetLabel(item) }}</div>
                                                <div v-if="assetSubLabel(item)"
                                                     class="small text-muted text-truncate">{{ assetSubLabel(item) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <!-- URS R30–R34：资产状态 / 资产名称 / 设备编码 / 成本中心 / 部门 全部由档案自动带出，只读 -->
                        <b-form-row>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('assetNo')">
                                    <b-form-input :value="pick('assetNo')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('subAssetNo')">
                                    <b-form-input :value="pick('subAssetNo')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('assetName')">
                                    <b-form-input :value="pick('assetName')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('equipmentCode')">
                                    <b-form-input :value="pick('equipmentCode')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <!-- 成本中心按「编码 名称」展示（如 20SRE001 RE成本中心），与固资各页口径一致 -->
                            <b-col md="3">
                                <b-form-group :label="plainLabel('costCenterName')">
                                    <b-form-input :value="costCenterText(pick('costCenterCode'), pick('costCenterName'))"
                                                  size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('deptName')">
                                    <b-form-input :value="pick('deptName')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('locationName')">
                                    <b-form-input :value="pick('locationName')" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                            <b-col md="3">
                                <b-form-group :label="plainLabel('assetStatus')">
                                    <b-form-input :value="assetStatusText" size="sm" readonly></b-form-input>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <h6 class="text-muted border-bottom pb-1">
                            {{ $t('label.em.asset.emArchFixedAssetChange.changeBlock') }}
                        </h6>
                        <b-form-row>
                            <b-col md="4">
                                <b-form-group :label="requiredLabel('changeType')">
                                    <b-form-select v-model="form.changeType" size="sm"
                                                   :options="changeTypeOptions"></b-form-select>
                                </b-form-group>
                            </b-col>

                            <b-col v-if="isAssetNameChange" md="4">
                                <b-form-group :label="requiredLabel('newAssetName')">
                                    <b-form-input v-model="form.newAssetName" size="sm" trim></b-form-input>
                                </b-form-group>
                            </b-col>

                            <b-col v-if="isEquipmentCodeChange" md="4">
                                <b-form-group :label="requiredLabel('newEquipmentCode')">
                                    <b-form-input v-model="form.newEquipmentCode" size="sm" trim></b-form-input>
                                </b-form-group>
                            </b-col>

                            <!--
                                口径：选「新成本中心」→ 系统按 MST_LOCATION 父节点推导「新部门」，
                                与 S5 后端 applyNewValues 的 COST_CENTER 分支、与档案编辑（R6/R7）方向一致。
                                注意 URS 需求_new R40/R41 写的是反方向（选新部门 → 带出新成本中心），
                                该冲突已记入 plan「已知约束」，留待 Finalize 向用户确认；
                                若最终改口径，改动点 = 后端 applyNewValues + 本页这两个下拉。
                            -->
                            <template v-if="isCostCenterChange">
                                <b-col md="4">
                                    <b-form-group :label="requiredLabel('newCostCenterId')">
                                        <b-form-select v-model="form.newCostCenterId" size="sm"
                                                       :options="costCenterOptions"></b-form-select>
                                    </b-form-group>
                                </b-col>
                                <b-col md="4">
                                    <b-form-group :label="plainLabel('newDeptName')">
                                        <b-form-input :value="newDeptName" size="sm" readonly></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </template>

                            <b-col md="8">
                                <b-form-group :label="plainLabel('remark')">
                                    <b-form-textarea v-model="form.remark" size="sm" rows="2"></b-form-textarea>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <div class="text-right">
                            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="goBack">
                                <i class="fa fa-fw fa-times"></i>
                                <span class="ml-1">{{ $t('label.em.asset.emArchFixedAssetChange.cancel') }}</span>
                            </b-button>
                            <b-button size="sm" variant="primary" :disabled="saving" @click="save">
                                <i class="fa fa-fw" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                                <span class="ml-1">{{ $t('label.em.asset.emArchFixedAssetChange.save') }}</span>
                            </b-button>
                        </div>
                    </b-form>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import emCommonApiMixin from "@/mixin/em/common/api";
import {costCenterText} from "@/views/em/asset/costCenterText";
import {CHANGE_TYPE_OPTIONS} from "@/model/em/asset/EmArchFixedAssetChangeModel";

/**
 * 固定资产变更 | 发起页（验收 #7；URS 需求_new R28–R41）。
 * <p>
 * 两步式发起（spec D12）：本页只 POST /emArchFixedAssetChange/save，
 * 保存成功后父类 afterSaveTrigger 会发 MQ 启动流程，申请人的「待办」里出现 task_change_init，
 * 在那里点「提交」才真正进入审批链。所以保存后跳到待办列表而不是留在本页。
 * <p>
 * 退回后的「修改重提」复用本页：路由带 ?id= 时先按 id 拉回原单再编辑。
 */
export default {
    name: "emArchFixedAssetChangeEditView",
    mixins: [emCommonApiMixin],
    data() {
        return {
            keyword: '',
            searching: false,
            //搜索结果浮层的开合与键盘高亮位；searchTimer 是输入防抖句柄
            suggestionsVisible: false,
            activeIndex: -1,
            searchTimer: null,
            saving: false,
            errorMessage: '',
            assets: [],
            costCenters: [],
            form: this.emptyForm(),
        }
    },
    computed: {
        baseApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAssetChange';
        },
        pageTitle() {
            return this.form.id
                    ? this.$t('title.em.asset.emArchFixedAssetChange.edit')
                    : this.$t('title.em.asset.emArchFixedAssetChange.create');
        },
        selectedAsset() {
            return this.assets.find(o => o.id === this.form.assetId) || null;
        },
        assetStatusText() {
            let status = this.pick('assetStatus');
            return status ? this.$t(`label.em.asset.emArchFixedAsset.status.${status}`) : '';
        },
        changeTypeOptions() {
            let options = [{
                value: null,
                text: this.$t('label.em.asset.emArchFixedAssetChange.changeTypePlaceholder')
            }];
            for (let item of CHANGE_TYPE_OPTIONS) {
                options.push({
                    value: item.value,
                    text: this.$t(`label.em.asset.emArchFixedAssetChange.type.${item.value}`)
                });
            }
            return options;
        },
        costCenterOptions() {
            let options = [{
                value: null,
                text: this.$t('label.em.asset.emArchFixedAsset.costCenterPlaceholder')
            }];
            for (let item of this.costCenters) {
                options.push({
                    value: item.id,
                    text: costCenterText(item.code, item.name)
                });
            }
            return options;
        },
        newDeptName() {
            let selected = this.costCenters.find(o => o.id === this.form.newCostCenterId);
            return selected ? selected.deptName : '';
        },
        isAssetNameChange() {
            return this.form.changeType === 'ASSET_NAME';
        },
        isEquipmentCodeChange() {
            return this.form.changeType === 'EQUIPMENT_CODE';
        },
        isCostCenterChange() {
            return this.form.changeType === 'COST_CENTER';
        },
    },
    mounted() {
        this.loadCostCenters();
        if (this.$route.query.id) {
            //修改重提：资产由原单带出，这里不能再补一次空关键字搜索——
            //它会和 loadExisting 里那次按固资号的搜索竞态，晚回来的一方把 assets 覆盖掉，
            //结果是框里留着资产标签、下面那排只读快照却空了
            this.loadExisting(this.$route.query.id);
        } else {
            this.searchAssets();
        }
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
    },
    methods: {
        costCenterText,

        emptyForm() {
            return {
                id: null,
                revision: null,
                assetId: null,
                changeType: null,
                newAssetName: null,
                newEquipmentCode: null,
                newCostCenterId: null,
                remark: null,
            };
        },

        /**
         * 搜索可发起变更的资产：后端只返回「正常」且无在途变更的资产（验收 #14），
         * 所以这里不需要再做状态过滤。
         * 修改重提时带上原单 id：原单锁定的资产的「在途变更」就是原单自己，不带的话它会被过滤掉，
         * 下面那排只读快照（含成本中心编码）回显为空。
         */
        searchAssets() {
            clearTimeout(this.searchTimer);
            this.searching = true;
            this.activeIndex = -1;
            let url = `${this.baseApi}/assets/search?keyword=${encodeURIComponent(this.keyword || '')}`;
            if (this.form.id) {
                url += `&changeId=${encodeURIComponent(this.form.id)}`;
            }
            // api/get 拿到的就是 body（EmResponseUtils.success 裸透传）
            return this.$_emApiMixin_get(url)
                    .then(res => this.assets = res || [])
                    .catch(() => this.assets = [])
                    .finally(() => this.searching = false);
        },

        /**
         * 浮层里一条候选的主文案；选中后原样写回输入框，保证「框里显示的」＝「提交的」。
         */
        assetLabel(item) {
            return `${item.assetNo || ''} / ${item.subAssetNo || ''} ${item.assetName || ''}`.trim();
        },

        /**
         * 副文案：同名资产靠设备编码 / 成本中心 / 部门区分。
         * 后端 default-property-inclusion = NON_NULL，缺的键整个消失，所以逐个判空再拼。
         */
        assetSubLabel(item) {
            // 成本中心与下面的只读框口径一致：「编码 名称」
            return [item.equipmentCode, costCenterText(item.costCenterCode, item.costCenterName), item.deptName]
                    .filter(o => !!o).join(' · ');
        },

        /**
         * 打字即搜（防抖 300ms）。同时作废已选资产：框里的文字一旦不再是那条资产的标签，
         * 下面那排只读快照就不该继续代表它。
         */
        onKeywordInput() {
            if (this.form.assetId) {
                this.form.assetId = null;
            }
            this.suggestionsVisible = true;
            this.activeIndex = -1;
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.searchAssets(), 300);
        },

        onAssetBlur() {
            this.suggestionsVisible = false;
            this.activeIndex = -1;
        },

        openSuggestions() {
            this.suggestionsVisible = true;
            this.$refs.assetInput.focus();
            this.searchAssets();
        },

        onAssetKeydown(event) {
            if (event.key === 'Escape') {
                this.suggestionsVisible = false;
                this.activeIndex = -1;
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (!this.suggestionsVisible) {
                    this.suggestionsVisible = true;
                    return;
                }
                if (this.assets.length === 0) {
                    return;
                }
                let step = event.key === 'ArrowDown' ? 1 : -1;
                this.activeIndex = (this.activeIndex + step + this.assets.length) % this.assets.length;
                return;
            }
            if (event.key === 'Enter') {
                //输入框在 b-form 里，回车不拦会走原生提交
                event.preventDefault();
                if (this.suggestionsVisible && this.activeIndex >= 0) {
                    this.selectAsset(this.assets[this.activeIndex]);
                } else {
                    this.suggestionsVisible = true;
                    this.searchAssets();
                }
            }
        },

        selectAsset(item) {
            this.form.assetId = item.id;
            this.keyword = this.assetLabel(item);
            this.suggestionsVisible = false;
            this.activeIndex = -1;
        },

        clearAsset() {
            this.form.assetId = null;
            this.keyword = '';
            this.suggestionsVisible = true;
            this.$refs.assetInput.focus();
            this.searchAssets();
        },

        /**
         * 把输入框文字对齐到当前已选资产。退回后修改重提时 assetId 来自原单，
         * 搜索结果回来前框里只有固资号，对齐后才是完整标签。
         */
        syncKeywordToSelection() {
            let asset = this.selectedAsset;
            if (asset) {
                this.keyword = this.assetLabel(asset);
            }
        },

        loadCostCenters() {
            this.$_emApiMixin_get(process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/costCenters')
                    .then(res => this.costCenters = res || []);
        },

        /**
         * 退回后修改重提：按 id 取回原单。
         * 走的是通用工作流查询（api/post 要 .data 一层），与 Task 页刷新时的取数路径一致。
         */
        loadExisting(id) {
            this.$_emApiMixin_post(`${this.baseApi}/workflow/find-by-query-form`, {
                idFilter: id,
                workflowQueryTypeFilter: 'todo'
            }).then(response => {
                let list = response.data && response.data.list;
                if (!list || list.length === 0) {
                    return;
                }
                let row = list[0];
                this.form = {
                    id: row.id,
                    revision: row.revision,
                    assetId: row.assetId,
                    changeType: row.changeType,
                    newAssetName: row.newAssetName,
                    newEquipmentCode: row.newEquipmentCode,
                    newCostCenterId: row.newCostCenterId,
                    remark: row.remark,
                };
                // 原单锁定的资产不一定在默认搜索结果里，用固资号再搜一次把它带进候选，
                // 回来后再把输入框文字对齐成完整标签
                if (row.assetNo) {
                    this.keyword = row.assetNo;
                    this.searchAssets().then(() => this.syncKeywordToSelection());
                }
            });
        },

        /**
         * 只读展示字段：优先取搜索结果里的资产快照。
         * 后端 default-property-inclusion = NON_NULL，null 字段是整个 key 消失，所以要兜 undefined。
         */
        pick(key) {
            let asset = this.selectedAsset;
            let value = asset ? asset[key] : null;
            return value === undefined || value === null ? '' : value;
        },

        save() {
            let missing = this.firstMissingRequired();
            if (missing) {
                this.errorMessage = `${this.$t('label.em.asset.emArchFixedAssetChange.requiredTip')}：${this.$t('label.em.asset.emArchFixedAssetChange.entity.' + missing)}`;
                return;
            }
            this.errorMessage = '';
            this.saving = true;
            // api/post 返回完整 response，业务体在 .data
            this.$_emApiMixin_post(`${this.baseApi}/save`, Object.assign({}, this.form))
                    .then(() => {
                        this.$bvToast.toast(this.$t('label.em.asset.emArchFixedAssetChange.saveSuccess'), {
                            variant: 'success',
                            noCloseButton: true,
                            autoHideDelay: 3000
                        });
                        this.goTodo();
                    })
                    .finally(() => this.saving = false);
        },

        /**
         * 必填按变更类型走（URS R36 / R38 / R40–R41）；
         * 快照字段与新部门由后端推导，不在前端校验。
         */
        firstMissingRequired() {
            if (!this.form.assetId) {
                return 'assetId';
            }
            if (!this.form.changeType) {
                return 'changeType';
            }
            if (this.isAssetNameChange && !this.form.newAssetName) {
                return 'newAssetName';
            }
            if (this.isEquipmentCodeChange && !this.form.newEquipmentCode) {
                return 'newEquipmentCode';
            }
            if (this.isCostCenterChange && !this.form.newCostCenterId) {
                return 'newCostCenterId';
            }
            return null;
        },

        goTodo() {
            this.$router.push({
                path: '/em/asset/emArchFixedAssetChangeList',
                query: {pathType: 'todo'}
            });
        },

        goBack() {
            this.$router.go(-1);
        },

        requiredLabel(key) {
            return `${this.$t('label.em.asset.emArchFixedAssetChange.entity.' + key)} *`;
        },

        plainLabel(key) {
            return this.$t('label.em.asset.emArchFixedAssetChange.entity.' + key);
        },
    }
};
</script>

<style scoped>
.asset-combo {
    position: relative;
}

.asset-dropdown {
    position: absolute;
    z-index: 1050;
    left: 0;
    right: 0;
    top: 100%;
    max-height: 260px;
    overflow-y: auto;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, .15);
    border-radius: .25rem;
    box-shadow: 0 .25rem .5rem rgba(0, 0, 0, .15);
}

.asset-item {
    padding: .25rem .5rem;
    cursor: pointer;
}

.asset-item-active {
    background-color: #f0f4ff;
}
</style>
